/**
 * SEO Utility Functions and Presets
 *
 * Separated from SEOHead.tsx to support React Fast Refresh and maintain clean component boundaries.
 */

import { SEOHeadProps } from './SEOHead';

/**
 * Preset SEO configurations for common page types
 */
export const SEOPresets = {
  home: (subdomain: 'houston' | 'lakecharles' | 'montana'): SEOHeadProps => {
    const config = {
      houston: {
        ogImage: '/media_houston/houston-logo-main.png',
        logo: '/media_houston/houston-logo-main.png',
      },
      lakecharles: {
        ogImage: '/media_lakecharles/og-lakecharles-1200x630.png',
        logo: '/media_lakecharles/icons/apple-touch-icon.png',
      },
      montana: {
        ogImage: '/og-montana-1200x630.png',
        logo: '/apple-touch-icon.png',
      },
    };

    const selected = config[subdomain] || config.houston;

    return {
      title: `${subdomain.charAt(0).toUpperCase() + subdomain.slice(1)} Community Platform`,
      description: `Empower ${subdomain} residents with tools to organize, voice opinions, and build collective power. Community-led platform for civic engagement.`,
      canonical: `https://${subdomain}.toolboxlogic.com/`,
      keywords: `${subdomain} community, civic engagement, community organizing, ${subdomain} residents, citizen empowerment, ToolboxLogic, Toolbox Logic, Tool Box, Toolb, Toolbox Houston, Toolbox Lake Charles, www.${subdomain}.toolboxlogic.com`,
      ogImage: selected.ogImage,
      jsonLd: generateOrganizationSchema(subdomain, selected.logo),
    };
  },

  about: (subdomain: string): SEOHeadProps => ({
    title: 'About Us',
    description:
      'Learn about ToolboxLogic and our mission to empower communities through technology and civic engagement tools.',
    canonical: `https://${subdomain}.toolboxlogic.com/about`,
    keywords: 'community platform, civic tech, community organizing tools',
  }),

  contact: (subdomain: string): SEOHeadProps => ({
    title: 'Contact Us',
    description:
      "Get in touch with ToolboxLogic. We're here to help your community organize and thrive.",
    canonical: `https://${subdomain}.toolboxlogic.com/contact`,
  }),

  services: (subdomain: string): SEOHeadProps => ({
    title: 'Community Services',
    description:
      "Explore the services and tools available to empower your community. From group buying to governance, we've got you covered.",
    canonical: `https://${subdomain}.toolboxlogic.com/services`,
    keywords: 'community services, group buying, civic engagement tools, community governance',
  }),
};

/**
 * Helper to generate JSON-LD organization schema
 */
export function generateOrganizationSchema(subdomain: string, logo?: string) {
  const logoUrl = logo
    ? logo.startsWith('http')
      ? logo
      : `https://${subdomain}.toolboxlogic.com${logo}`
    : `https://${subdomain}.toolboxlogic.com/apple-touch-icon.png`;

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'ToolboxLogic',
    alternateName: ['Toolbox Logic', 'Tool Box', 'Toolbox', 'Toolb', 'ToolboxLogic Community'],
    url: `https://${subdomain}.toolboxlogic.com`,
    logo: logoUrl,
    description: 'Community platform for civic engagement and collective action',
    sameAs: ['https://twitter.com/toolboxlogic', 'https://facebook.com/toolboxlogic'],
  };
}

/**
 * Helper to generate JSON-LD local business schema
 */
export function generateLocalBusinessSchema(
  cityName: string,
  stateName: string,
  subdomain: string,
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: `ToolboxLogic ${cityName}`,
    alternateName: [`Toolbox ${cityName}`, `Tool Box ${cityName}`, `Toolb ${cityName}`],
    description: `Community platform serving ${cityName}, ${stateName}`,
    url: `https://${subdomain}.toolboxlogic.com`,
    address: {
      '@type': 'PostalAddress',
      addressLocality: cityName,
      addressRegion: stateName,
      addressCountry: 'US',
    },
  };
}
